// Admin Dashboard Web Component

class AdminDashboard extends HTMLElement {
  constructor() {
    super();
    this.currentUser = null;
    this.isAdmin = false;
  }

  connectedCallback() {
    this.render();
    this.setupAuthListener();
    this.setupRouteHandling();
  }

  async setupAuthListener() {
    // Listen for auth-status-loaded event from main.js
    this.handleAuthStatusLoaded = (e) => {
      const { user, authenticated } = e.detail;

      if (authenticated && user) {
        this.currentUser = user;
        this.isAdmin = user.role === 'admin';

        if (this.isAdmin) {
          this.renderAdminDashboard();
          this.updateActiveTabFromRoute();
          this.setCurrentUserOnActiveComponent();
        } else {
          this.renderUnauthorized();
        }
      } else {
        this.currentUser = null;
        this.isAdmin = false;
        this.renderUnauthorized();
      }
    };

    document.addEventListener('auth-status-loaded', this.handleAuthStatusLoaded);

    // Listen for auth-success event to update immediately after login
    this.handleAuthSuccess = (e) => {
      const { user } = e.detail;
      if (user) {
        window.logger.info('auth-success in admin-dashboard', user);
        this.currentUser = user;
        this.isAdmin = user.role === 'admin';

        if (this.isAdmin) {
          this.renderAdminDashboard();
          this.updateActiveTabFromRoute();
          this.setCurrentUserOnActiveComponent();
        } else {
          this.renderUnauthorized();
        }
      }
    };

    document.addEventListener('auth-success', this.handleAuthSuccess);
  }

  render() {
    this.innerHTML = `
      <div class="admin-dashboard">
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Checking admin permissions...</div>
        </div>
      </div>
    `;
  }

  renderUnauthorized() {
    this.innerHTML = `
      <div class="admin-dashboard">
        <div class="unauthorized-state">
          <div class="unauthorized-icon">🚫</div>
          <h2 class="unauthorized-title">Access Denied</h2>
          <p class="unauthorized-message">
            You don't have administrator privileges to access this section.
          </p>
          <a href="/" class="btn btn-primary">Return to Home</a>
        </div>
      </div>

    `;
  }

  renderAdminDashboard() {
    this.innerHTML = `
      <div class="admin-dashboard">
        <div class="admin-content">
          <div id="audit-logs-content">
            <audit-logs></audit-logs>
          </div>
          <div id="user-management-content" style="display: none;">
            <user-management></user-management>
          </div>
          <div id="prompt-management-content" style="display: none;">
            <prompt-management></prompt-management>
          </div>
          <div id="site-stats-content" style="display: none;">
            <site-stats></site-stats>
          </div>
          <div id="image-flags-content" style="display: none;">
            <image-flags></image-flags>
          </div>
          <div id="blocked-domains-content" style="display: none;">
            <blocked-domains></blocked-domains>
          </div>
          <div id="oauth-clients-content" style="display: none;">
            <oauth-clients></oauth-clients>
          </div>
        </div>
      </div>

    `;

    this.attachEventListeners();
  }

  setupRouteHandling() {
    // Listen for route changes to update active tab
    window.addEventListener('popstate', () => {
      this.updateActiveTabFromRoute();
    });

    // Listen for navigation events from the router
    document.addEventListener('section-shown', (e) => {
      if (e.detail.section === 'admin') {
        // Ensure admin dashboard is rendered before updating route
        if (this.isAdmin) {
          setTimeout(() => this.updateActiveTabFromRoute(), 0);
        }
      }
    });

    // Initial route check
    this.updateActiveTabFromRoute();
  }

  updateActiveTabFromRoute() {
    const currentPath = window.location.pathname;

    // Determine active tab based on route
    let activeTab = 'audit-logs'; // default
    if (currentPath === '/admin/users') {
      activeTab = 'user-management';
    } else if (currentPath === '/admin/prompts') {
      activeTab = 'prompt-management';
    } else if (currentPath === '/admin/stats') {
      activeTab = 'site-stats';
    } else if (currentPath === '/admin/image-flags') {
      activeTab = 'image-flags';
    } else if (currentPath === '/admin/blocked-domains') {
      activeTab = 'blocked-domains';
    } else if (currentPath === '/admin/oauth-clients') {
      activeTab = 'oauth-clients';
    } else if (currentPath === '/admin/audit-logs' || currentPath === '/admin') {
      activeTab = 'audit-logs';
    }

    this.switchTab(activeTab);
  }

  attachEventListeners() {
    // Tab navigation is now handled by the router
    // Remove old click handlers since we're using links
  }

  switchTab(tabName) {
    // Update active tab
    this.querySelectorAll('.admin-tab').forEach(tab => {
      tab.classList.toggle('active', tab.dataset.tab === tabName);
    });

    // Show/hide content
    this.querySelectorAll('[id$="-content"]').forEach(content => {
      content.style.display = 'none';
    });

    const activeContent = this.querySelector(`#${tabName}-content`);
    if (activeContent) {
      activeContent.style.display = 'block';

      // Set current user on the active component
      this.setCurrentUserOnActiveComponent();
    }
  }

  setCurrentUserOnActiveComponent() {
    // Find the currently active content div
    const activeContent = this.querySelector('[id$="-content"][style*="block"]');
    if (activeContent && this.currentUser) {
      const component = activeContent.querySelector('*');
      if (component && component.setCurrentUser) {
        component.setCurrentUser(this.currentUser);
      }
    }
  }

  disconnectedCallback() {
    // Remove event listeners when component is removed
    if (this.handleAuthStatusLoaded) {
      document.removeEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    }
    if (this.handleAuthSuccess) {
      document.removeEventListener('auth-success', this.handleAuthSuccess);
    }
  }

  setCurrentUser(user) {
    this.currentUser = user;
    this.isAdmin = user.role === 'admin';
    this.renderAdminDashboard();
    this.updateActiveTabFromRoute();
    this.setCurrentUserOnActiveComponent();
  }

}

// Register the custom element
customElements.define('admin-dashboard', AdminDashboard);
